<template>
  <f7-card class="item-card">
    <f7-card-content>
      <div class="item-header">
        <div class="item-name">{{ item.name }}</div>
        <div class="item-price">
          {{ fmtCfa(effectivePrice) }}
          <span v-if="thresholdReached" class="item-orig">{{ fmtCfa(item.unitPrice) }}</span>
        </div>
      </div>

      <f7-progressbar v-if="item.thresholdQty" class="tier-progress" :progress="tierPct" />
      <div v-if="item.thresholdQty" class="tier-caption">
        {{ item.currentQty }} / {{ item.thresholdQty }} {{ $t('campaign.unitsDash') }}
        {{ thresholdReached ? $t('campaign.groupPriceUnlocked') : $t('campaign.moreToUnlock', { n: remaining, price: fmtCfa(item.groupPrice) }) }}
      </div>

      <div class="qty-row">
        <f7-button round raised class="qty-btn" @click="qty = Math.max(1, qty - 1)">−</f7-button>
        <span class="qty-val">{{ qty }}</span>
        <f7-button round raised   class="qty-btn" @click="qty++">+</f7-button>
        <f7-button small round raised  fill class="commit-btn" @click="commit">
          {{ $t('campaign.addToCart', { price: fmtCfa(effectivePrice * qty) }) }}
        </f7-button>
      </div>
    </f7-card-content>
  </f7-card>
</template>

<script setup>
import { ref, computed } from 'vue'
import { f7 } from 'framework7-vue'
import client from '../../js/api/client.js'
import { useKeycloakStore } from '../../js/pinia-stores/keycloak.js'
import i18n from '../../js/i18n.js'

const { t } = i18n.global

const props = defineProps({ item: { type: Object, required: true } })
const emit = defineEmits(['committed'])

const keycloak = useKeycloakStore()
const qty = ref(1)

const thresholdReached = computed(() =>
  props.item.thresholdQty != null && props.item.currentQty >= props.item.thresholdQty)

const effectivePrice = computed(() =>
  thresholdReached.value && props.item.groupPrice
    ? props.item.groupPrice : props.item.unitPrice)

const remaining = computed(() =>
  Math.max(0, (props.item.thresholdQty ?? 0) - props.item.currentQty))

const tierPct = computed(() => {
  if (!props.item.thresholdQty) return 0
  return Math.min(100, Math.round((props.item.currentQty / props.item.thresholdQty) * 100))
})

function fmtCfa(n) { return `${Math.round(n).toLocaleString('fr-FR')} ${props.item.currency}` }

async function commit() {
  f7.preloader.show()
  try {
    const { data } = await client.post(
      `/campaigns/items/${props.item.id}/commit`, {
        userId: keycloak.subject,
        quantity: qty.value,
      })
    f7.preloader.hide()
    emit('committed', data)
    f7.toast.show({ text: t('campaign.quantityAdded'), closeTimeout: 1500 })
  } catch (e) {
    f7.preloader.hide()
    f7.dialog.alert(e?.response?.data?.message || t('campaign.orderFailed'))
  }
}
</script>

<style scoped>
.item-card { margin: 0 0 10px; }
.item-header { display: flex; justify-content: space-between; align-items: baseline; }
.item-name { font-size: 14px; font-weight: 500; }
.item-price { font-size: 14px; font-weight: 500; color: var(--f7-theme-color); }
.item-orig { font-size: 11px; color: var(--f7-on-surface-variant, #8e8e93); text-decoration: line-through; margin-left: 4px; }
.tier-progress {
  --f7-progressbar-height: 5px;
  --f7-progressbar-border-radius: 3px;
  --f7-progressbar-bg-color: var(--f7-list-item-border-color, #e0e0e0);
  --f7-progressbar-progress-color: var(--f7-theme-color);
  margin: 8px 0 4px;
}
.tier-caption { font-size: 11px; color: var(--f7-on-surface-variant, #8e8e93); }
.qty-row { display: flex; align-items: center; gap: 8px; margin-top: 10px; }
.qty-btn {
  min-width: 0;
  width: 28px; height: 28px; padding: 0;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 7px;
  border: 1px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
  background: none; font-size: 16px; line-height: 1;
}
.qty-val { font-weight: 500; min-width: 18px; text-align: center; }
.commit-btn { margin-left: auto; }
</style>
